import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { fetchProductPrices, type StripePriceMap } from '../utils/stripe';
import { shopData } from '../components/pages/site/shopData';
import { bookData } from '../components/pages/site/bookData';
interface PriceContextValue {
  prices: StripePriceMap;
  loading: boolean;
  error: string | null;
  /** Look up a single price by Stripe `lookup_key`; returns null if not loaded */
  getPriceCents: (lookupKey: string | undefined) => number | null;
  /** Manually refresh prices from Stripe */
  refresh: () => Promise<void>;
}
const PriceContext = createContext<PriceContextValue | undefined>(undefined);
/** Collect every known Stripe lookup_key across the catalog */
function getAllLookupKeys(): string[] {
  const keys = new Set<string>();
  for (const p of shopData) {
    if (p.lookupKey) keys.add(p.lookupKey);
  }
  for (const b of bookData) {
    if (b.lookupKey) keys.add(b.lookupKey);
  }
  return Array.from(keys);
}
export function PriceProvider({
  children
}: {
  children: ReactNode;
}) {
  const [prices, setPrices] = useState<StripePriceMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    const lookupKeys = getAllLookupKeys();
    if (lookupKeys.length === 0) {
      setPrices({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchProductPrices(lookupKeys);
      setPrices(result);
    } catch (err: any) {
      // Silent failure is fine — components fall back to display prices
      console.warn('[Prices] Could not fetch live prices from Stripe:', err?.message ?? err);
      setError(err?.message ?? 'Could not load prices');
    } finally {
      setLoading(false);
    }
  }, []);
  // Fetch on mount
  useEffect(() => {
    refresh();
  }, [refresh]);
  // Refetch when tab regains focus — keeps prices fresh after price changes
  useEffect(() => {
    const onFocus = () => {
      refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);
  const getPriceCents = useCallback((lookupKey: string | undefined): number | null => {
    if (!lookupKey) return null;
    return prices[lookupKey]?.amount ?? null;
  }, [prices]);
  const value: PriceContextValue = {
    prices,
    loading,
    error,
    getPriceCents,
    refresh
  };
  return <PriceContext.Provider value={value} data-id="element-2850">{children}</PriceContext.Provider>;
}
export function usePrices() {
  const ctx = useContext(PriceContext);
  if (!ctx) throw new Error('usePrices must be used inside <PriceProvider>');
  return ctx;
}
/**
 * Convenience hook to get a single product's current price (in cents).
 * Looks up by Stripe `lookup_key`. Returns:
 *   - the live Stripe price if loaded
 *   - the provided fallback price (display hint) if Stripe hasn't loaded
 *   - null if neither is available
 */
export function usePrice(lookupKey: string | undefined, fallbackCents?: number): {
  cents: number | null;
  isLive: boolean;
  loading: boolean;
} {
  const {
    prices,
    loading
  } = usePrices();
  const live = lookupKey ? prices[lookupKey]?.amount : undefined;
  if (live !== undefined) {
    return {
      cents: live,
      isLive: true,
      loading
    };
  }
  if (fallbackCents !== undefined) {
    return {
      cents: fallbackCents,
      isLive: false,
      loading
    };
  }
  return {
    cents: null,
    isLive: false,
    loading
  };
}